import { InputAdornment, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateReportMutation } from '../../../../hooks/mutations/useReportMutation';
import { Report } from '../../../../services/apiSLTP/activities';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getReportTypeInfos, reportTypeEnum } from '../../../../utils/enums';
import CustomTimePicker from '../../../CustomTimePicker';
import DropDown from '../../../DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';
import ToggleLine from '../../../ToggleLine';
import CustomTextInput from '../../../CustomTextInput';
import IconButton from '../../../shared/IconButton';

interface EditReportModalProps extends ModalProps {
    report: Report | null;
    refetch?: () => void;
}

const EditReportModal: React.FC<EditReportModalProps> = ({ open, handleClose, report, refetch = () => {} }) => {
    const { t } = useTranslation();

    const [selectedReportType, setSelectedReportType] = useState({ label: '', value: '' });
    const [workStartTime, setWorkStartTime] = useState('');
    const [workEndTime, setWorkEndTime] = useState('');
    const [extraHours, setExtraHours] = useState('');
    const [isLongTrip, setIsLongTrip] = useState(false);

    const { mutate: updateReport } = useUpdateReportMutation({
        onSettled: () => {
            refetch();
            handleClose();
        },
    });

    const reportTypeOptions = Object.values(reportTypeEnum)
        .filter((value) => typeof value === 'number')
        .map((value) => ({
            label: capitalizeFirstLetter(t(getReportTypeInfos(value as number))),
            value: String(value),
        }));

    useEffect(() => {
        if (report) {
            setSelectedReportType({
                label: capitalizeFirstLetter(t(getReportTypeInfos(report.reportType))),
                value: String(report.reportType),
            });
            setWorkStartTime(report.workStartTime || '');
            setWorkEndTime(report.workEndTime || '');
            setExtraHours(String(report.extraHours || '0'));
            setIsLongTrip(report.isLongTrip || false);
        }
    }, [report, t]);

    const handleUpdateReport = () => {
        if (!report) return;

        updateReport({
            id: report._id,
            body: {
                reportType: Number(selectedReportType.value),
                workStartTime: workStartTime,
                workEndTime: workEndTime,
                extraHours: Number(extraHours) || 0,
                isLongTrip: isLongTrip,
            },
        });
    };

    if (!report) return null;

    return (
        <ModalBody
            closeText={t('cancel')}
            okText={t('validate')}
            title={t('report')}
            subTitle={t('edit')}
            description={formatName(report.userId.firstName, report.userId.lastName)}
            open={open}
            handleClose={handleClose}
            handleOk={handleUpdateReport}
            disabledOk={!selectedReportType.value}
        >
            <DropDown
                selectedItem={selectedReportType}
                items={reportTypeOptions}
                handleChange={(event, value) => setSelectedReportType({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('state')}
                required
            />

            {/* <CustomTimePicker
                label={capitalizeFirstLetter(t('startDate'))}
                value={workStartTime}
                onChange={(isoString) => setWorkStartTime(isoString)}
                required={false}
            />

            <CustomTimePicker
                label={capitalizeFirstLetter(t('endDate'))}
                value={workEndTime}
                onChange={(isoString) => setWorkEndTime(isoString)}
                required={false}
            />*/}
            {selectedReportType.value !== String(reportTypeEnum.ABSENT) && (
                <>
                    <CustomTextInput
                        label={capitalizeFirstLetter(t('overtime'))}
                        type="number"
                        value={extraHours}
                        onChange={(value) => {
                            const num = parseFloat(value);
                            if (isNaN(num)) return setExtraHours('');
                            const rounded = Math.round(num * 2) / 2;
                            setExtraHours(rounded.toString());
                        }}
                        required
                        min={-24}
                        max={24}
                        step={0.5}
                        startAdornment={
                            <InputAdornment position="start">
                                <IconButton
                                    iconName="remove"
                                    disabled={parseFloat(extraHours) <= -24}
                                    onClick={() => setExtraHours(Math.max(-24, (parseFloat(extraHours) ?? 0.5) - 0.5).toString())}
                                />
                            </InputAdornment>
                        }
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    iconName="add"
                                    disabled={parseFloat(extraHours) >= 24}
                                    onClick={() => setExtraHours(Math.min(24, (parseFloat(extraHours) ?? 0) + 0.5).toString())}
                                />
                            </InputAdornment>
                        }
                    />
                    <ToggleLine checked={isLongTrip} onChange={(checked) => setIsLongTrip(checked)} title={capitalizeFirstLetter(t('longTrip'))} />
                </>
            )}
        </ModalBody>
    );
};

export default EditReportModal;
